import React, { useState } from 'react'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { getPlaceholderImage, getDarkerPlaceholderImage, getFallbackImage } from '../utils/natureImages'
import FileUpload from './FileUpload'

const { FiVideo, FiImage, FiFileText, FiMusic, FiEdit3, FiTrash2, FiSave, FiX, FiPlus, FiRotateCcw } = FiIcons

const ClassCard = ({ classData, scheduleInfo, isAuthenticated, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isFlipped, setIsFlipped] = useState(false)
  const [editData, setEditData] = useState({
    zoom_link: classData?.zoom_link || '',
    zoom_link_2: classData?.zoom_link_2 || '',
    notes: classData?.notes || '',
    audio_link: classData?.audio_link || '',
    images: Array.isArray(classData?.images) ? classData.images : []
  })
  const [saving, setSaving] = useState(false)
  const [newImageUrl, setNewImageUrl] = useState('')

  // Safely check for content
  const hasContent = classData && (
    classData.zoom_link || 
    classData.zoom_link_2 ||
    classData.notes || 
    classData.audio_link || 
    (Array.isArray(classData.images) && classData.images.length > 0)
  )

  const handleSave = async () => {
    setSaving(true)
    try {
      // Ensure images is always an array and filter out empty URLs
      const safeEditData = {
        ...editData,
        images: Array.isArray(editData.images) ? 
          editData.images.filter(img => img && img.trim()) : []
      }

      let result
      if (!classData?.id) {
        // Create new class if it doesn't exist
        const newClassData = {
          class_number: scheduleInfo.classNumber,
          class_date: scheduleInfo.date.toISOString(),
          ...safeEditData
        }
        result = await onUpdate(null, newClassData)
      } else {
        result = await onUpdate(classData.id, safeEditData)
      }
      
      if (result?.success) {
        setIsEditing(false)
      } else {
        alert('Error saving class: ' + (result?.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error saving class:', error)
      alert('Error saving class: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleImageUrlAdd = () => {
    if (newImageUrl && newImageUrl.trim()) {
      const url = newImageUrl.trim()
      // Basic URL validation
      if (url.startsWith('http://') || url.startsWith('https://')) {
        setEditData(prev => ({
          ...prev,
          images: [...(Array.isArray(prev.images) ? prev.images : []), url]
        }))
        setNewImageUrl('')
      } else {
        alert('Please enter a valid URL starting with http:// or https://')
      }
    }
  }

  const handleImageRemove = (index) => {
    setEditData(prev => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== index)
    }))
  }

  const handleFileUploadComplete = (uploadResults) => {
    const newUrls = uploadResults.map(result => result.url).filter(url => url)
    if (newUrls.length > 0) {
      setEditData(prev => ({
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ...newUrls]
      }))
    }
  }

  const validateImageUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'))
  }

  // Show beautiful nature placeholder for students when no content
  if (!hasContent && !isAuthenticated) {
    return (
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-800/20 overflow-hidden relative min-h-[320px]">
        {/* Full Color Background Nature Image - No Opacity Reduction */}
        <div className="absolute inset-0">
          <img
            src={getPlaceholderImage(scheduleInfo.classNumber)}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = getFallbackImage()
            }}
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/50"></div>
        </div>
        
        {/* Content with enhanced readability over full color image */}
        <div className="relative z-10 p-6 h-full min-h-[320px] flex flex-col justify-between">
          <div>
            <h3 className="text-xl font-bold text-white mb-2 drop-shadow-lg">
              Class {scheduleInfo.classNumber}
            </h3>
            <p className="text-white/90 font-medium mb-4 drop-shadow-md">
              {scheduleInfo.dayName}, {scheduleInfo.formattedDate}
            </p>
          </div>
          
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-4 border border-white/30">
              <SafeIcon icon={FiFileText} className="text-white text-2xl drop-shadow-lg" />
            </div>
            <p className="text-white font-semibold text-lg drop-shadow-lg mb-2">
              Class content will be available soon
            </p>
            <p className="text-white/80 text-sm drop-shadow-md">
              Check back after the class session
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative group min-h-[520px]">
      <div className={`flip-card ${isFlipped ? 'flipped' : ''} h-full`}>
        {/* Front Side - Content */}
        <div className="flip-card-front bg-white rounded-xl shadow-sm border-2 border-gray-800/20 overflow-hidden relative h-full">
          {/* Much Darker background nature image for all cards with content */}
          <div className="absolute inset-0 opacity-5">
            <img
              src={getDarkerPlaceholderImage(scheduleInfo.classNumber)}
              alt=""
              className="w-full h-full object-cover filter brightness-50 contrast-125"
              onError={(e) => {
                e.target.src = getFallbackImage()
              }}
            />
          </div>

          <div className="relative z-10 h-full flex flex-col bg-gradient-to-br from-white/99 via-white/97 to-white/95 backdrop-blur-sm">
            {/* Header Section - Fixed */}
            <div className="flex-shrink-0 p-6 pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Class {scheduleInfo.classNumber}
                  </h3>
                  <p className="text-gray-600">
                    {scheduleInfo.dayName}, {scheduleInfo.formattedDate}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Flip Button - Only show if has content */}
                  {hasContent && (
                    <button
                      onClick={() => setIsFlipped(!isFlipped)}
                      className="p-2 text-gray-400 hover:text-green-500 transition-colors"
                      title="View nature image"
                    >
                      <SafeIcon icon={FiRotateCcw} className="text-lg" />
                    </button>
                  )}
                  
                  {isAuthenticated && (
                    <>
                      {!isEditing ? (
                        <>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                          >
                            <SafeIcon icon={FiEdit3} className="text-lg" />
                          </button>
                          {classData && (
                            <button
                              onClick={() => onDelete(classData.id)}
                              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="text-lg" />
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="p-2 text-gray-400 hover:text-green-500 transition-colors disabled:opacity-50"
                          >
                            <SafeIcon icon={FiSave} className="text-lg" />
                          </button>
                          <button
                            onClick={() => setIsEditing(false)}
                            disabled={saving}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          >
                            <SafeIcon icon={FiX} className="text-lg" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="space-y-6">
                {/* Zoom Recordings */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <SafeIcon icon={FiVideo} className="text-blue-500" />
                    <span className="font-medium text-gray-700">Zoom Recordings</span>
                  </div>
                  {isEditing ? (
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={editData.zoom_link || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, zoom_link: e.target.value }))}
                        placeholder="https://zoom.us/rec/share/... (Primary Recording)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white/95 backdrop-blur-sm text-sm"
                      />
                      <input
                        type="url"
                        value={editData.zoom_link_2 || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, zoom_link_2: e.target.value }))}
                        placeholder="https://zoom.us/rec/share/... (Secondary Recording - Optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white/95 backdrop-blur-sm text-sm"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {classData?.zoom_link && (
                        <a
                          href={classData.zoom_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm mr-2 mb-2 text-sm"
                        >
                          <SafeIcon icon={FiVideo} className="mr-2" />
                          {classData.zoom_link_2 ? 'Primary Recording' : 'Watch Recording'}
                        </a>
                      )}
                      {classData?.zoom_link_2 && (
                        <a
                          href={classData.zoom_link_2}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm"
                        >
                          <SafeIcon icon={FiVideo} className="mr-2" />
                          Secondary Recording
                        </a>
                      )}
                      {!classData?.zoom_link && !classData?.zoom_link_2 && (
                        <p className="text-gray-500 italic text-sm">No recordings available</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <SafeIcon icon={FiFileText} className="text-green-500" />
                    <span className="font-medium text-gray-700">Notes</span>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={editData.notes || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Class notes..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none bg-white/95 backdrop-blur-sm text-sm"
                    />
                  ) : (
                    <div>
                      {classData?.notes ? (
                        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 border border-gray-100 shadow-sm max-h-32 overflow-y-auto">
                          <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{classData.notes}</p>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic text-sm">No notes available</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Audio */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <SafeIcon icon={FiMusic} className="text-purple-500" />
                    <span className="font-medium text-gray-700">Audio</span>
                  </div>
                  {isEditing ? (
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={editData.audio_link || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, audio_link: e.target.value }))}
                        placeholder="https://example.com/audio.mp3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white/95 backdrop-blur-sm text-sm"
                      />
                      <FileUpload
                        type="audio"
                        classNumber={scheduleInfo.classNumber}
                        accept="audio/*"
                        onUploadComplete={(results) => {
                          if (results.length > 0) {
                            setEditData(prev => ({ ...prev, audio_link: results[0].url }))
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <div>
                      {classData?.audio_link ? (
                        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 border border-gray-100 shadow-sm">
                          <audio controls className="w-full">
                            <source src={classData.audio_link} type="audio/mpeg" />
                            <source src={classData.audio_link} type="audio/wav" />
                            <source src={classData.audio_link} type="audio/ogg" />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic text-sm">No audio available</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Images */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <SafeIcon icon={FiImage} className="text-orange-500" />
                    <span className="font-medium text-gray-700">Images</span>
                  </div>
                  {isEditing ? (
                    <div className="space-y-3">
                      {/* URL Input */}
                      <div className="flex space-x-2">
                        <input
                          type="url"
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleImageUrlAdd()
                            }
                          }}
                          placeholder="https://example.com/image.jpg"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white/95 backdrop-blur-sm text-sm"
                        />
                        <button
                          type="button"
                          onClick={handleImageUrlAdd}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                        >
                          <SafeIcon icon={FiPlus} />
                        </button>
                      </div>

                      {/* File Upload */}
                      <FileUpload
                        type="image"
                        classNumber={scheduleInfo.classNumber}
                        accept="image/*"
                        multiple={true}
                        onUploadComplete={handleFileUploadComplete}
                        className="text-sm"
                      />

                      {/* Image Grid - Scrollable */}
                      {Array.isArray(editData.images) && editData.images.length > 0 && (
                        <div className="max-h-48 overflow-y-auto">
                          <div className="grid grid-cols-2 gap-2">
                            {editData.images.map((image, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={image}
                                  alt={`Class image ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg shadow-sm"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200x150?text=Image+Error'
                                    e.target.className += ' opacity-50'
                                  }}
                                />
                                <button
                                  onClick={() => handleImageRemove(index)}
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  <SafeIcon icon={FiX} />
                                </button>
                                {!validateImageUrl(image) && (
                                  <div className="absolute bottom-1 left-1 px-1 py-0.5 bg-yellow-500 text-white text-xs rounded shadow-sm">
                                    Invalid URL
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {Array.isArray(classData?.images) && classData.images.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto">
                          <div className="grid grid-cols-2 gap-2">
                            {classData.images.map((image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt={`Class image ${index + 1}`}
                                className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                                onClick={() => window.open(image, '_blank')}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Found'
                                  e.target.className += ' opacity-50'
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic text-sm">No images available</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back Side - Nature Image */}
        <div className="flip-card-back bg-white rounded-xl shadow-sm border-2 border-gray-800/20 overflow-hidden relative h-full">
          <div className="absolute inset-0">
            <img
              src={getPlaceholderImage(scheduleInfo.classNumber)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = getFallbackImage()
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/40"></div>
          </div>
          
          <div className="relative z-10 p-6 h-full min-h-[520px] flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                  Class {scheduleInfo.classNumber}
                </h3>
                <p className="text-white/90 font-medium drop-shadow-md">
                  {scheduleInfo.dayName}, {scheduleInfo.formattedDate}
                </p>
              </div>
              <button
                onClick={() => setIsFlipped(false)}
                className="p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors border border-white/30"
                title="Back to content"
              >
                <SafeIcon icon={FiRotateCcw} className="text-lg" />
              </button>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-4 border border-white/30">
                <SafeIcon icon={FiImage} className="text-white text-2xl drop-shadow-lg" />
              </div>
              <p className="text-white font-semibold text-lg drop-shadow-lg mb-2">
                Nature View
              </p>
              <p className="text-white/80 text-sm drop-shadow-md">
                Beautiful nature imagery for Class {scheduleInfo.classNumber}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .flip-card {
          background-color: transparent;
          width: 100%;
          height: 100%;
          perspective: 1000px;
        }
        
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          transition: transform 0.6s;
        }
        
        .flip-card-back {
          transform: rotateY(180deg);
        }
        
        .flip-card.flipped .flip-card-front {
          transform: rotateY(-180deg);
        }
        
        .flip-card.flipped .flip-card-back {
          transform: rotateY(0deg);
        }
        
        /* Custom scrollbar for content areas */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 3px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  )
}

export default ClassCard